import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  paidMediaOverviewService,
  type PaidMediaOverviewAttributionConfidenceData,
  type PaidMediaOverviewAttributionMode,
  type PaidMediaOverviewEfficiencyScaleData,
  type PaidMediaOverviewFilterParams,
  type PaidMediaOverviewFiltersData,
  type PaidMediaOverviewFunnelData,
  type PaidMediaOverviewGranularity,
  type PaidMediaOverviewMeta,
  type PaidMediaOverviewPlatformFilter,
  type PaidMediaOverviewRevenueValidationData,
  type PaidMediaOverviewSummaryData,
  type PaidMediaOverviewTrendsData,
  type PaidMediaOverviewWarning,
} from "src/services/PaidMediaOverviewAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { useAsyncResource } from "src/hooks/UseAsyncResource";

export interface UsePaidMediaOverviewParams {
  startDate: string;
  endDate: string;
  granularity: PaidMediaOverviewGranularity;
  platform: PaidMediaOverviewPlatformFilter;
  attributionMode: PaidMediaOverviewAttributionMode;
  campaignId: string;
  objectiveOrNetwork: string;
  status: string;
}

export interface PaidMediaOverviewBundle {
  summary: PaidMediaOverviewSummaryData | null;
  trends: PaidMediaOverviewTrendsData | null;
  funnel: PaidMediaOverviewFunnelData | null;
  efficiencyScale: PaidMediaOverviewEfficiencyScaleData | null;
  revenueValidation: PaidMediaOverviewRevenueValidationData | null;
  attributionConfidence: PaidMediaOverviewAttributionConfidenceData | null;
  filters: PaidMediaOverviewFiltersData | null;
  currency: string | null;
  appliedAttributionMode: PaidMediaOverviewAttributionMode;
  shopifyAvailable: boolean;
  warnings: PaidMediaOverviewWarning[];
  errors: string[];
  summaryLoading: boolean;
  trendsLoading: boolean;
  funnelLoading: boolean;
  efficiencyScaleLoading: boolean;
  revenueValidationLoading: boolean;
  attributionConfidenceLoading: boolean;
  attributionConfidenceErrorCode: string | null;
  coreLoading: boolean;
}

const SUPPRESSED_WARNING_CODES = new Set([
  "ATTRIBUTION_MAPPING_UNVERIFIED",
  "CAMPAIGN_VIEW_NOT_SHOPIFY_VALIDATED",
]);

const collectWarnings = (
  metas: Array<PaidMediaOverviewMeta | null | undefined>
): PaidMediaOverviewWarning[] => {
  const seen = new Set<string>();
  const warnings: PaidMediaOverviewWarning[] = [];

  metas.forEach(meta => {
    (meta?.warnings ?? []).forEach(warning => {
      if (!warning?.message || SUPPRESSED_WARNING_CODES.has(warning.code)) {
        return;
      }
      const key = `${warning.code ?? ""}|${warning.message}`;
      if (!seen.has(key)) {
        seen.add(key);
        warnings.push(warning);
      }
    });
  });

  return warnings;
};

const collectErrors = (errors: Array<string | null>): string[] => {
  const seen = new Set<string>();
  return errors.filter((error): error is string => {
    if (!error || seen.has(error)) {
      return false;
    }
    seen.add(error);
    return true;
  });
};

export function usePaidMediaOverview({
  startDate,
  endDate,
  granularity,
  platform,
  attributionMode,
  campaignId,
  objectiveOrNetwork,
  status,
}: UsePaidMediaOverviewParams): PaidMediaOverviewBundle {
  const { t, i18n } = useTranslation();
  const fallbackError = t("paidMediaOverview.errors.requestFailed");
  const rangeReady = Boolean(startDate && endDate);
  /**
   * Part of every request below *and* of their dependency lists: the overview endpoints return
   * copy the dashboard renders as-is, so switching language has to re-fetch. `useAsyncResource`
   * re-runs when the request callback's identity changes, which is what these deps drive.
   */
  const lang = getApiLangFromI18n(i18n);

  const filterParams = useMemo<PaidMediaOverviewFilterParams>(
    () => ({
      platform,
      campaign_id: campaignId || undefined,
      objective_or_network: objectiveOrNetwork || undefined,
      status: status || undefined,
    }),
    [platform, campaignId, objectiveOrNetwork, status]
  );

  const summaryRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchSummary({
        start_date: startDate,
        end_date: endDate,
        attribution_mode: attributionMode,
        lang,
        ...filterParams,
      }),
    [startDate, endDate, attributionMode, filterParams, lang]
  );

  const trendsRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchTrends({
        start_date: startDate,
        end_date: endDate,
        granularity,
        attribution_mode: attributionMode,
        lang,
        ...filterParams,
      }),
    [startDate, endDate, granularity, attributionMode, filterParams, lang]
  );

  const funnelRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchFunnel({
        start_date: startDate,
        end_date: endDate,
        attribution_mode: attributionMode,
        lang,
        ...filterParams,
      }),
    [startDate, endDate, attributionMode, filterParams, lang]
  );

  const efficiencyScaleRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchEfficiencyScale({
        start_date: startDate,
        end_date: endDate,
        attribution_mode: attributionMode,
        lang,
        ...filterParams,
      }),
    [startDate, endDate, attributionMode, filterParams, lang]
  );

  const revenueValidationRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchRevenueValidation({
        start_date: startDate,
        end_date: endDate,
        lang,
        ...filterParams,
      }),
    [startDate, endDate, filterParams, lang]
  );

  const attributionConfidenceRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchAttributionConfidence({
        start_date: startDate,
        end_date: endDate,
        platform,
        lang,
      }),
    [startDate, endDate, platform, lang]
  );

  const filtersRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchFilters({
        start_date: startDate,
        end_date: endDate,
        platform,
        lang,
      }),
    [startDate, endDate, platform, lang]
  );

  const summary = useAsyncResource(rangeReady ? summaryRequest : null, fallbackError);
  const trends = useAsyncResource(rangeReady ? trendsRequest : null, fallbackError);
  const funnel = useAsyncResource(rangeReady ? funnelRequest : null, fallbackError);
  const efficiencyScale = useAsyncResource(
    rangeReady ? efficiencyScaleRequest : null,
    fallbackError
  );
  const revenueValidation = useAsyncResource(
    rangeReady ? revenueValidationRequest : null,
    fallbackError
  );
  const filters = useAsyncResource(rangeReady ? filtersRequest : null, fallbackError);

  const attributionConfidence = useAsyncResource(
    rangeReady ? attributionConfidenceRequest : null,
    fallbackError
  );

  const warnings = useMemo(
    () =>
      collectWarnings([
        summary.data?.meta,
        trends.data?.meta,
        funnel.data?.meta,
        efficiencyScale.data?.meta,
        revenueValidation.data?.meta,
        attributionConfidence.data?.meta,
        filters.data?.meta,
      ]),
    [
      summary.data,
      trends.data,
      funnel.data,
      efficiencyScale.data,
      revenueValidation.data,
      attributionConfidence.data,
      filters.data,
    ]
  );

  const errors = useMemo(
    () =>
      collectErrors([
        summary.error,
        trends.error,
        funnel.error,
        efficiencyScale.error,
        revenueValidation.error,
        attributionConfidence.errorCode === "NO_SOURCE_AVAILABLE" ? null : attributionConfidence.error,
        filters.error,
      ]),
    [
      summary.error,
      trends.error,
      funnel.error,
      efficiencyScale.error,
      revenueValidation.error,
      attributionConfidence.error,
      attributionConfidence.errorCode,
      filters.error,
    ]
  );

  /**
   * `/filters` narrows `campaigns` by platform, but each option also carries its own `platform`,
   * so scope the list here as well. The dropdown must never offer another platform's campaign,
   * and the dashboard clears a stale selection against this same list. Options without a
   * `platform` are kept — an unclassifiable entry shouldn't silently disappear.
   */
  const filtersData = useMemo(() => {
    const data = filters.data?.data ?? null;
    if (!data || platform === "all") {
      return data;
    }
    return {
      ...data,
      campaigns: (data.campaigns ?? []).filter(
        campaign => !campaign?.platform || campaign.platform === platform
      ),
    };
  }, [filters.data, platform]);

  const currency =
    summary.data?.meta?.currency ??
    trends.data?.meta?.currency ??
    efficiencyScale.data?.meta?.currency ??
    revenueValidation.data?.meta?.currency ??
    null;

  return {
    summary: summary.data?.data ?? null,
    trends: trends.data?.data ?? null,
    funnel: funnel.data?.data ?? null,
    efficiencyScale: efficiencyScale.data?.data ?? null,
    revenueValidation: revenueValidation.data?.data ?? null,
    attributionConfidence: attributionConfidence.data?.data ?? null,
    filters: filtersData,
    currency,
    appliedAttributionMode: summary.data?.meta?.attribution_mode ?? attributionMode,
    shopifyAvailable: revenueValidation.data?.meta?.shopify_available === true,
    warnings,
    errors,
    summaryLoading: summary.loading,
    trendsLoading: trends.loading,
    funnelLoading: funnel.loading,
    efficiencyScaleLoading: efficiencyScale.loading,
    revenueValidationLoading: revenueValidation.loading,
    attributionConfidenceLoading: attributionConfidence.loading,
    attributionConfidenceErrorCode: attributionConfidence.errorCode,
    coreLoading: summary.loading,
  };
}
